import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import useApiQuery from '../../hooks/useApiQuery'
import { fetchEngageDashboard } from '../../lib/api'
import { PageHeader, StatCard, Card, DataTable, StatusBadge, Skeleton } from '../../components/ui'

export default function EngageDashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const { data, loading } = useApiQuery(() => fetchEngageDashboard())

  const stats    = data?.stats    || {}
  const contacts = data?.recent_contacts || []
  const campaigns= data?.recent_campaigns|| []

  const hour = new Date().getHours()
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening'

  return (
    <div>
      <PageHeader
        title={`${greeting}, ${user?.name?.split(' ')[0] || 'there'} 👋`}
        subtitle="Here's what's happening with your contacts and campaigns"
      >
        <button className="btn-primary" onClick={() => navigate('/engage/campaigns')}>
          + New Campaign
        </button>
      </PageHeader>

      {/* Plan banner */}
      {user?.plan === 'free' && (
        <div className="mb-6 rounded-2xl p-4 flex items-center justify-between border"
          style={{ background: 'rgba(0,229,200,0.04)', borderColor: 'rgba(0,229,200,0.2)' }}>
          <div>
            <p className="font-semibold text-white text-sm">You're on the Free plan</p>
            <p className="text-xs text-[var(--text-dim)] mt-0.5">Upgrade to Growth for unlimited contacts and campaigns.</p>
          </div>
          <button className="btn-primary flex-shrink-0" onClick={() => navigate('/engage/billing')}>
            Upgrade
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total Contacts"  value={loading ? '—' : (stats.contacts    ?? 0).toLocaleString()} icon="👥" color="#00E5C8" loading={loading} />
        <StatCard label="Campaigns Sent"  value={loading ? '—' : stats.campaigns    ?? 0}  icon="📧" color="#1A6AF5" loading={loading} />
        <StatCard label="Active Workflows"value={loading ? '—' : stats.workflows    ?? 0}  icon="⚡" color="#A855F7" loading={loading} />
        <StatCard label="Open Rate"       value={loading ? '—' : `${stats.open_rate ?? 0}%`} icon="📊" color="#F59E0B" loading={loading} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Contacts */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-sm text-white">Recent Contacts</h3>
            <button className="text-xs text-[var(--color-accent)] hover:underline" onClick={() => navigate('/engage/contacts')}>View all →</button>
          </div>
          <DataTable
            loading={loading}
            data={contacts}
            emptyText="No contacts yet"
            emptyIcon="👥"
            columns={[
              { key: 'name',   label: 'Name',   render: r => <span className="text-xs text-white">{r.name || r.email}</span> },
              { key: 'source', label: 'Source', render: r => <span className="text-[11px] font-mono text-[var(--text-dim)]">{r.source || '—'}</span> },
              { key: 'status', label: 'Status', render: r => <StatusBadge status={r.status || 'active'} /> },
              { key: 'lead_score', label: 'Score', render: r => (
                <span className="text-xs font-mono" style={{ color: (r.lead_score || 0) > 70 ? '#34d399' : (r.lead_score || 0) > 40 ? '#fbbf24' : '#94a3b8' }}>
                  {r.lead_score ?? '—'}
                </span>
              )},
            ]}
            onRowClick={r => navigate(`/engage/contacts/${r.id || r._id}`)}
          />
        </Card>

        {/* Recent Campaigns */}
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-bold text-sm text-white">Recent Campaigns</h3>
            <button className="text-xs text-[var(--color-accent)] hover:underline" onClick={() => navigate('/engage/campaigns')}>View all →</button>
          </div>
          <DataTable
            loading={loading}
            data={campaigns}
            emptyText="No campaigns yet"
            emptyIcon="📧"
            columns={[
              { key: 'name',       label: 'Name',      render: r => <span className="text-xs text-white truncate max-w-[140px] block">{r.name}</span> },
              { key: 'status',     label: 'Status',    render: r => <StatusBadge status={r.status} /> },
              { key: 'sent_count', label: 'Sent',      render: r => <span className="text-xs font-mono text-[var(--text-muted)]">{(r.sent_count ?? 0).toLocaleString()}</span> },
              { key: 'open_rate',  label: 'Open %',    render: r => <span className="text-xs font-mono text-emerald-400">{r.open_rate ? `${r.open_rate}%` : '—'}</span> },
            ]}
            onRowClick={r => navigate(`/engage/campaigns/${r.id || r._id}`)}
          />
        </Card>
      </div>

      {/* Quick actions */}
      <Card className="mt-6">
        <h3 className="font-display font-bold text-sm text-white mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: '📧', label: 'New Campaign',    sub: 'Email, SMS, WhatsApp',  to: '/engage/campaigns' },
            { icon: '⚡', label: 'New Workflow',     sub: 'Automate sequences',    to: '/engage/workflows' },
            { icon: '🌐', label: 'Add Domain',       sub: 'Connect your website',  to: '/engage/domains' },
            { icon: '📊', label: 'View Analytics',   sub: 'Track performance',     to: '/engage/analytics' },
          ].map(a => (
            <button key={a.label} onClick={() => navigate(a.to)}
              className="card-hover flex items-center gap-3 p-3.5 text-left group">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                style={{ background: 'rgba(0,229,200,0.1)', border: '1px solid rgba(0,229,200,0.2)' }}>
                {a.icon}
              </div>
              <div>
                <p className="text-xs font-semibold text-white group-hover:text-[#00E5C8] transition-colors">{a.label}</p>
                <p className="text-[10px] text-[var(--text-dim)] mt-0.5">{a.sub}</p>
              </div>
            </button>
          ))}
        </div>
      </Card>
    </div>
  )
}
